/* eslint-disable no-console */

"use client";

import { useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { QuickSignIn } from "@/src/components/modals/QuickSignIn";
import { countries } from "@/src/constants/conutries";
import { CookieKeys } from "@/src/enums/CookieKeys.enum";
import {
  analyticsCreate,
  brevoCreateUserActionLog,
  CreateAnalyticsDtoAction,
  CreateAnalyticsDtoEntityType,
  CreateAnalyticsDtoType,
  CreateUserActionLogDtoActionType,
  CreateUserActionLogDtoEntityType,
  CreateUserActionLogDtoUserType,
  JobResponseEntity,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";
import { useCookies } from "@/src/hooks/useCookies";
import { logBrazeCustomEvent } from "@/utils/brazeClient";

import {
  AutomaticApplyHandoffDecision,
  useAutomaticApplyHandoffEligibility,
} from "../hooks/useAutomaticApplyHandoffEligibility";
import {
  emitHandoffEvents,
  getHandoffSessionSuppression,
  IAutomaticApplyHandoffBeacon,
  markHandedOffThisSession,
  navigateToApplyUrl,
} from "../utils/automaticApplyHandoff";
import {
  AutomaticApplySkipReason,
  describeError,
  IAutomaticApplySkip,
} from "../utils/automaticApplySkipReason";
import { buildJobApplyUrl } from "../utils/buildJobApplyUrl";
import { buildJobViewEvents } from "../utils/buildJobViewEvents";

import { AutomaticApplyInterstitial } from "./AutomaticApplyInterstitial";

// Only ever used for the server render, which runs no effects and so records
// no view; the client computes its own value on hydration.
const SERVER_RENDER_SUPPRESSION: IAutomaticApplySkip = {
  reason: AutomaticApplySkipReason.UNKNOWN,
  detail: "server render",
};

export function JobDetailClientWrapper({
  jobDetails,
}: {
  jobDetails: JobResponseEntity;
}): JSX.Element {
  const { isAuthenticated, authData } = useAuth();
  const searchParams = useSearchParams();
  const { setCookie, getCookie, removeCookie } = useCookies();

  // Read once, synchronously, so an eligible job is never briefly shown as a
  // normal page before the hand-off takes over. On the server this is always
  // suppressed, which keeps the interstitial out of the rendered HTML.
  const [sessionSuppression] = useState(() =>
    typeof window === "undefined"
      ? SERVER_RENDER_SUPPRESSION
      : getHandoffSessionSuppression(jobDetails.id)
  );
  const [isMounted, setIsMounted] = useState(false);
  const eligibility = useAutomaticApplyHandoffEligibility(
    jobDetails,
    sessionSuppression
  );
  // Set when the hand-off itself throws. It overrides an "eligible" decision so
  // the candidate gets the normal page, and the view is recorded through the
  // ordinary path with the error, instead of no view at all.
  const [handoffFailure, setHandoffFailure] = useState<IAutomaticApplySkip>();
  const handoff: AutomaticApplyHandoffDecision = handoffFailure
    ? { status: "ineligible", skip: handoffFailure }
    : eligibility;
  const handoffStatus = handoff.status;
  const hasStartedHandoff = useRef(false);
  // The job whose view has been sent through the ordinary path. The view effect
  // depends on several values that can change identity after the view is sent,
  // and none of those changes is a new view.
  const viewRecordedForJobId = useRef<number>();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [isForceUserToLoginToViewJob, setIsForceUserToLoginToViewJob] =
    useState(false);

  // LinkedIn quick login disabled if want to enable again just change disabled-quick-login into src
  const source = searchParams.get("disabled-quick-login");
  const jobSlug = getCookie(CookieKeys.JobSlug);

  useEffect(() => {
    if (jobSlug) {
      removeCookie(CookieKeys.JobSlug);
    }
    if (isAuthenticated === undefined || isAuthenticated) {
      return;
    }
    if (source === "linkedin") {
      setCookie({
        key: CookieKeys.JobSlug,
        value: jobDetails.slug,
        maxAge: 600,
      });
      setIsForceUserToLoginToViewJob(true);
    }
  }, [
    jobDetails.slug,
    setCookie,
    source,
    isAuthenticated,
    jobSlug,
    removeCookie,
  ]);

  useEffect(() => {
    // Wait until auth has resolved (isAuthenticated is undefined while the
    // profile is still hydrating). Firing earlier logs a logged-in user as a
    // non-member because authData isn't populated yet.
    if (isAuthenticated === undefined) {
      return;
    }
    // Also wait for the hand-off decision, so an automatic visit records its
    // view as automated too. Firing now would stamp auto_redirect false on the
    // view and true on the click for the same visit. Traffic without a
    // utm_source resolves to "ineligible" synchronously, so this does not delay
    // the ordinary case.
    if (handoffStatus === "pending") {
      return;
    }
    if (viewRecordedForJobId.current === jobDetails.id) {
      return;
    }
    const isAutomatedVisit = handoffStatus === "eligible";
    if (jobDetails) {
      logBrazeCustomEvent("JOB_VIEWED", {
        id: jobDetails.id,
        title: jobDetails.title,
        org_id: jobDetails.organization.id,
        org_name: jobDetails.organization.name,
        location: `${jobDetails.locations.location}, ${
          countries[jobDetails.locations.country]
        }`,
        type: jobDetails.type,
        industry: jobDetails.industry.name,
      });

      // An automatic visit records its view in the hand-off effect instead, as
      // a beacon alongside the click. Sent from here as an ordinary request it
      // is cancelled when the hand-off navigates away, losing the view.
      if (isAutomatedVisit) {
        return;
      }
      viewRecordedForJobId.current = jobDetails.id;

      try {
        let uuid: string | undefined;
        if (!authData) {
          if (!getCookie(CookieKeys.UUID)) {
            uuid = uuidv4();
            setCookie({
              key: CookieKeys.UUID,
              value: uuid,
              maxAge: 30 * 60, // 30 minutes in seconds
            });
          } else {
            uuid = getCookie(CookieKeys.UUID);
          }
        }
        const nonMemberUuid: string | undefined = getCookie(
          CookieKeys.NonMemberUUID
        );
        const viewEvents = buildJobViewEvents({
          jobDetails,
          authData,
          isAuthenticated,
          uuid,
          nonMemberUuid,
          searchParams,
          isAutomatedVisit,
          handoffSkip: handoff.skip,
        });
        analyticsCreate(viewEvents.analytics);
        if (viewEvents.userActionLog) {
          brevoCreateUserActionLog(viewEvents.userActionLog);
        }
      } catch (error) {
        console.log(
          `Error while creating analytics", ${error}, job id  ${jobDetails.id},user: id ${authData?.id}`
        );
      }
    }
  }, [
    jobDetails,
    authData,
    isAuthenticated,
    analyticsCreate,
    handoffStatus,
    handoff.skip,
  ]);

  useEffect(() => {
    if (handoffStatus !== "eligible" || hasStartedHandoff.current) {
      return;
    }
    // Wait for auth to resolve, otherwise a signed-in candidate is handed off
    // and recorded as anonymous because authData has not populated yet.
    if (isAuthenticated === undefined) {
      return;
    }
    hasStartedHandoff.current = true;
    try {
      const applyUrl = buildJobApplyUrl(jobDetails);
      const apiUrl = process.env.API_URL;
      let uuid = getCookie(CookieKeys.UUID);
      if (!authData && !uuid) {
        uuid = uuidv4();
        setCookie({
          key: CookieKeys.UUID,
          value: uuid,
          maxAge: 30 * 60, // 30 minutes in seconds
        });
      }
      const nonMemberUuid: string | undefined = getCookie(
        CookieKeys.NonMemberUUID
      );
      const analyticsPayload = {
        entity_id: jobDetails.id,
        entity_type: CreateAnalyticsDtoEntityType.jobs,
        type: CreateAnalyticsDtoType.job_apply_btn,
        action: CreateAnalyticsDtoAction.click,
        uuid,
        utm_source: searchParams.get("utm_source") ?? undefined,
        utm_medium: searchParams.get("utm_medium") ?? undefined,
        utm_campaign: searchParams.get("utm_campaign") ?? undefined,
        utm_term: searchParams.get("utm_term") ?? undefined,
        utm_content: searchParams.get("utm_content") ?? undefined,
        auto_redirect: true,
      };
      const viewEvents = buildJobViewEvents({
        jobDetails,
        authData,
        isAuthenticated,
        uuid,
        nonMemberUuid,
        searchParams,
        isAutomatedVisit: true,
      });
      // The view is queued first so it normally reaches the API ahead of the
      // click. Beacon arrival order is not guaranteed, so a click can still be
      // stamped before its view.
      const beacons: IAutomaticApplyHandoffBeacon[] = [
        { url: `${apiUrl}/analytics`, payload: { ...viewEvents.analytics } },
      ];
      if (viewEvents.userActionLog) {
        beacons.push({
          url: `${apiUrl}/brevo/user-action-log`,
          payload: { ...viewEvents.userActionLog },
        });
      }
      // Mirrors the branches in jobsDetailAction's jobTracking: anonymous
      // candidates go through /analytics/update so an existing popup row is
      // converted rather than duplicated, signed-in ones straight to /analytics.
      beacons.push(
        authData
          ? {
              url: `${apiUrl}/analytics`,
              payload: { ...analyticsPayload, userId: authData.id },
            }
          : { url: `${apiUrl}/analytics/update`, payload: analyticsPayload }
      );
      if (isAuthenticated || nonMemberUuid) {
        beacons.push({
          url: `${apiUrl}/brevo/user-action-log`,
          payload: {
            action_type: CreateUserActionLogDtoActionType.JOB_APPLIED,
            entity_type: CreateUserActionLogDtoEntityType.job,
            entity_id: jobDetails.id,
            uuid: (authData ? authData.uuid : nonMemberUuid) ?? "",
            user_type: authData
              ? CreateUserActionLogDtoUserType.member
              : CreateUserActionLogDtoUserType["non-member"],
            auto_redirect: true,
          },
        });
      }
      emitHandoffEvents(beacons).finally(() => {
        // Written before navigating so returning to this job in the same session
        // renders the normal page instead of handing off again.
        markHandedOffThisSession(jobDetails.id);
        navigateToApplyUrl(applyUrl);
      });
    } catch (error) {
      // Only the synchronous preparation above can land here: emitHandoffEvents
      // is async, so its own failures become a rejection and never reach this
      // catch. No beacon has been queued, so falling back to the normal page
      // records exactly one view.
      setHandoffFailure({
        reason: AutomaticApplySkipReason.HANDOFF_ERROR,
        detail: describeError(error),
      });
    }
  }, [
    handoffStatus,
    isAuthenticated,
    authData,
    jobDetails,
    searchParams,
    getCookie,
    setCookie,
  ]);

  if (isMounted && handoffStatus !== "ineligible") {
    return (
      <AutomaticApplyInterstitial
        organizationName={jobDetails.organization.name}
        applyUrl={buildJobApplyUrl(jobDetails)}
      />
    );
  }

  return <QuickSignIn isOpen={isForceUserToLoginToViewJob} />;
}
